/* eslint-disable @typescript-eslint/naming-convention */
import React, { useState, useReducer, useEffect, createContext, useContext } from 'react'
import api from 'api'
import { logout as logoutFromMPDC } from 'phonecom'
import { stubDevices } from './mock'
import { passwordSecurityReducer, deviceReducer, clearSessionsReducer, DeviceReducerState, ClearSessionsReducerState, PasswordSecurityInterface, PasswordSecurityReducerState, passwordSecurityDefaultData, deviceDefaultData, clearSessionsDefaultData, supressionsDefaultData, SupressionsReducerState, supressionsReducer } from './reducers'
import AccountSecurityClient from './AccountSecurityClient'
import { usePhoenixSessionContext, UserContext } from 'providers'
import API from 'users-app/src/util/api_v5'

/**
 *
 */
export { stubDevices } from './mock'

const MFA_FEATURE = 'mfa.signin_email_required_new_customers'
/**
 *
 */
export const PASSWORD_EXPIRY_POPUP_SUPPRESSION_KEY = 'password_expiry_popup'

/**
 *
 */
export type DeviceInterface = {
    id: number
    name: string
    currentDevice?: boolean
    location: string
    date: string
    type: 'Desktop' | 'Mobile'
}

type ResetTypes = 'PASSWORD_DATA' | 'DEVICES' | 'CLEAR_SESSIONS'

interface ClientInterface extends AccountSecurityClient {
    enableMFA: (options?: { requestNewSession?: boolean }) => Promise<any>
    _sessionToken: string
    setSessionToken: (token: string) => void
    getPasswordInfo: () => Promise<Record<string, any>>
}

interface ContextInterface {
    passwordSecurityState: PasswordSecurityReducerState,
    updateSecurityData: (data: Partial<PasswordSecurityInterface>) => void,
    devices: DeviceReducerState, setDevices: (revalidate?: boolean) => Promise<DeviceInterface[]>,
    clearSessions: ClearSessionsReducerState, clearDeviceSessions: (clearCurrentSession?: boolean, logout?: () => any) => Promise<any>,
    reset: (type: ResetTypes) => void,
    client: ClientInterface,
    supressions: SupressionsReducerState
}

/**
 *
 */
export const AccountSecurityContext = createContext<ContextInterface | null>(null)

/**
 *
 */
export const useAccountSecurityContext = () => {
    const contextData = useContext(AccountSecurityContext)
    if (!contextData) {
        throw new Error('useAccountSecurityContext must be used inside AccountSecurityProvider tags')
    }

    return contextData
}

/**
 *
 */
export const AccountSecurityProvider = ({ children }: { children: JSX.Element }) => {
    const [passwordSecurityState, dispatchPasswordSecurity] = useReducer(passwordSecurityReducer, { data: passwordSecurityDefaultData, isLoading: true, error: '' })
    const [devices, dispatchDevices] = useReducer(deviceReducer, { data: deviceDefaultData, isLoading: false, error: '' })
    const [clearSessions, dispatchClearSessions] = useReducer(clearSessionsReducer, { data: clearSessionsDefaultData, isLoading: false, error: '' })
    const [supressions, dispatchSupressions] = useReducer(supressionsReducer, { data: supressionsDefaultData, isLoading: false, error: '' })
    const { session } = usePhoenixSessionContext()
    const [client, setClient] = useState(new AccountSecurityClient() as ClientInterface)
    const user = useContext(UserContext)

    const getData = (response: Record<string, any>): PasswordSecurityInterface => {
        return response.data
    }

    const findDateDifference = (date: string) => {
        if (!date) return null

        const now = new Date()
        const expiryDate = new Date(date)
        const difference = expiryDate.getTime() - now.getTime()
        return Math.ceil(difference / (1000 * 60 * 60 * 24))
    }

    const setPasswordSecurityData = async () => {
        let data

        try {
            dispatchPasswordSecurity({ type: 'SET_LOADING', payload: true })

            const isMfaEnabled = user?.features?.includes(MFA_FEATURE)
            const mfaEnableLastDate = user?.mfa_grace_period_end

            const [passwordInfoResponse, reminder_keys] = await Promise.all([client.getPasswordInfo(), setSupressionsData()])

            let passwordInfo: Record<string, any> = {}
            if (passwordInfoResponse.success) {
                passwordInfo = passwordInfoResponse.data || {}
            }

            let passwordExpiryDialogShown = false
            if (reminder_keys.length) {
                passwordExpiryDialogShown = reminder_keys.includes(PASSWORD_EXPIRY_POPUP_SUPPRESSION_KEY)
            }

            const response = await new Promise<Record<string, any>>((resolve) => {
                resolve({
                    data: {
                        hasPasswordExpired: passwordInfo.is_password_expired || false,
                        daysRemainedForPasswordExpiry: findDateDifference(passwordInfo.password_expiry_date || null),
                        passwordExpiryDate: passwordInfo.password_expiry_date || null,
                        passwordExpiryDialogShown,
                        isMfaEnabled,
                        mfaEnableLastDate
                    }
                })
            })
            data = getData(response)

            dispatchPasswordSecurity({ type: 'SET_DATA', payload: data })
        } catch (error) {
            console.error('Failed to fetch password related data:', error)
            dispatchPasswordSecurity({ type: 'SET_ERROR', payload: error.message })
        } finally {
            dispatchPasswordSecurity({ type: 'SET_LOADING', payload: false })
        }

        return data
    }

    const setSupressionsData = async () => {
        let data = []

        try {
            dispatchSupressions({ type: 'SET_LOADING', payload: true })

            const response = await API.getSupressedData()
            data = response.data.reminder_keys || []

            dispatchSupressions({ type: 'SET_DATA', payload: data })
        } catch (error) {
            console.error('Failed to fetch supressions related data:', error)
            dispatchSupressions({ type: 'SET_ERROR', payload: (error as Error).message })
        } finally {
            dispatchSupressions({ type: 'SET_LOADING', payload: false })
        }

        return data
    }

    const setDevices = async (revalidate = false) => {
        if (!revalidate && devices.data.length) return devices.data

        let fetchedDevices

        try {
            dispatchDevices({ type: 'SET_LOADING', payload: true })

            const response = await api.fetchActiveSessions()
            fetchedDevices = response.data || stubDevices

            dispatchDevices({ type: 'SET_DATA', payload: fetchedDevices })
        } catch (error) {
            console.error('Failed to fetch device session related data:', error)
            dispatchDevices({ type: 'SET_ERROR', payload: (error as Error).message })
        } finally {
            dispatchDevices({ type: 'SET_LOADING', payload: false })
        }

        return fetchedDevices
    }

    const clearDeviceSessions = async (clearCurrentSession = false, logoutCb) => {
        let data

        try {
            dispatchClearSessions({ type: 'SET_LOADING', payload: true })

            const logoutPromise = logoutCb ? logoutCb() : logoutFromMPDC(false)
            const response = await api.clearDeviceSessions()
            if (clearCurrentSession) await logoutPromise

            data = response.data || {}

            dispatchClearSessions({ type: 'SET_DATA', payload: data })
        } catch (error) {
            console.error('Failed to clear device sessions:', error)
            dispatchClearSessions({ type: 'SET_ERROR', payload: (error as Error).message })
        } finally {
            dispatchClearSessions({ type: 'SET_LOADING', payload: false })
        }

        return data
    }

    const updateSecurityData = (data: Partial<PasswordSecurityInterface>) => {
        dispatchPasswordSecurity({ type: 'SET_DATA', payload: { ...passwordSecurityState.data, ...data } })
    }

    const reset = (type: ResetTypes) => {
        switch (type) {
                case 'PASSWORD_DATA':
                    dispatchPasswordSecurity({ type: 'RESET' })
                    break
                case 'DEVICES':
                    dispatchDevices({ type: 'RESET' })
                    break
                case 'CLEAR_SESSIONS':
                    dispatchClearSessions({ type: 'RESET' })
                    break
                default:
                    console.warn('Invalid Reset Type')
                    break
        }
    }

    const setSessionToken = (token: string) => {
        const newClient = new AccountSecurityClient() as ClientInterface
        token = token.startsWith('Bearer ') ? token.split('Bearer ')[1] : token
        newClient.setSessionToken(token)
        setClient(newClient)
    }

    useEffect(() => {
        if (user && client?._sessionToken) {
            setPasswordSecurityData()
        }
    }, [user, client])

    useEffect(() => {
        if (session.user?.token) setSessionToken(session.user.token)
    }, [session.user?.token])

    return (
        <AccountSecurityContext.Provider value={{ passwordSecurityState, updateSecurityData, devices, setDevices, clearSessions, clearDeviceSessions, reset, client, supressions }}>
            {children}
        </AccountSecurityContext.Provider>
    )
}

/**
 *
 */
export { passwordSecurityDefaultData, clearSessionsDefaultData } from './reducers'
